import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { API_BASE_URL } from '../../config';
import { AuthShell } from '../../components/AuthShell';

type Invitation = {
  email: string;
  orgName: string;
  role: string;
  departmentName?: string;
  inviterName?: string;
};

export const AcceptInvitationPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('invitation') || '';

  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!invitationToken) {
        setError('No invitation token provided');
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/invitation-validate?token=${encodeURIComponent(invitationToken)}`);
        const data = (await response.json().catch(() => ({}))) as any;

        if (!response.ok) throw new Error(data.error || 'Invalid invitation');

        setInvitation(data as Invitation);
        sessionStorage.setItem('pendingInvitation', invitationToken);
      } catch (e: any) {
        setError(e?.message || 'Invalid invitation');
      } finally {
        setIsLoading(false);
      }
    };

    void load();
  }, [invitationToken]);

  return (
    <AuthShell maxWidth={520}>
      {isLoading ? (
        <div className="text-center">
          <div className="spinner-border mb-3" role="status" />
          <p className="np-sub mb-0">Loading invitation…</p>
        </div>
      ) : error ? (
        <>
          <div className="auth-heading">
            <h3>Invalid invitation</h3>
          </div>
          <div className="np-banner np-tone-danger mb-4"><i className="ri-error-warning-line"></i>{error}</div>
          <div className="text-center">
            <button className="np-btn np-btn-ghost" onClick={() => navigate('/login')}>
              Back to Sign In
            </button>
          </div>
        </>
      ) : invitation ? (
        <>
          <div className="auth-heading">
            <h3>You're invited!</h3>
            <p>You've been invited to join <strong style={{ color: 'var(--bs-heading-color)' }}>{invitation.orgName}</strong></p>
          </div>

          <div className="np-field">
            <label>Email</label>
            <input className="np-input" value={invitation.email} readOnly />
          </div>

          <div className="np-field">
            <label>Role</label>
            <input className="np-input" value={invitation.role} readOnly />
          </div>

          {invitation.departmentName ? (
            <div className="np-field">
              <label>Department</label>
              <input className="np-input" value={invitation.departmentName} readOnly />
            </div>
          ) : null}

          <div className="np-field">
            <label>Invited By</label>
            <input className="np-input" value={invitation.inviterName || 'Admin'} readOnly />
          </div>

          <p className="np-sub text-center mb-3">Sign in to accept this invitation and join the organization</p>

          <button className="np-btn np-btn-primary w-100 justify-content-center" onClick={() => navigate('/login')}>
            Continue to Sign In
          </button>
        </>
      ) : null}
    </AuthShell>
  );
};
